import { graphql, useLazyLoadQuery } from "react-relay";
import type { TooltipContentProps } from "recharts";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Text } from "@phoenix/components";
import {
  ChartEmptyStateOverlay,
  ChartTooltip,
  ChartTooltipItem,
  compactChartMargin,
  compactLegendProps,
  compactTimeXAxisProps,
  compactYAxisProps,
  defaultCartesianGridProps,
  defaultTooltipProps,
  InteractiveLegend,
  TimeRangeChartBrush,
  useBinTimeTickFormatter,
  useInteractiveLegend,
  useSequentialChartColors,
} from "@phoenix/components/chart";
import { useTimeBinScale } from "@phoenix/hooks/useTimeBin";
import { useTimeFormatters } from "@phoenix/hooks/useTimeFormatters";
import { useUTCOffsetMinutes } from "@phoenix/hooks/useUTCOffsetMinutes";
import type { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import {
  PROJECT_METRICS_CHART_SYNC_ID,
  useMetricQueryFetchOptions,
} from "@phoenix/pages/project/metrics/types";
import {
  formatFloat,
  latencyMsFormatter,
} from "@phoenix/utils/numberFormatUtils";

import type { SpanAttributePercentilesTimeSeriesQuery } from "./__generated__/SpanAttributePercentilesTimeSeriesQuery.graphql";

function TooltipContent({ active, payload, label }: TooltipContentProps) {
  const { fullTimeFormatter } = useTimeFormatters();
  if (active && payload && payload.length) {
    return (
      <ChartTooltip>
        {label && (
          <Text weight="heavy" size="S">{`${fullTimeFormatter(
            new Date(Number(label))
          )}`}</Text>
        )}
        {payload.map((entry, index) => {
          if (entry.value == null) return null;
          return (
            <ChartTooltipItem
              key={index}
              color={entry.color}
              shape="line"
              name={String(entry.dataKey || "unknown")}
              value={`${formatFloat(Number(entry.value))} s`}
            />
          );
        })}
      </ChartTooltip>
    );
  }

  return null;
}

export type SpanAttributePercentilesTimeSeriesProps = ProjectMetricViewProps & {
  /**
   * Path segments into the span's attributes, e.g. `["tts", "ttfb"]`.
   */
  attributePath: readonly string[];
  /**
   * Narrows to the span that carries the attribute. Always pass one: an
   * unfiltered query sorts every span in the time range.
   */
  filterCondition: string;
};

/**
 * Percentiles of a numeric span attribute over time.
 *
 * Modelled on {@link TraceLatencyPercentilesTimeSeries}, with one deliberate
 * difference: trace latency is stored in milliseconds and divided by 1000 for
 * display, whereas these attributes are published in seconds already (matching
 * livekit's own unit, and consistent across `llm.ttft`, `tts.ttfb`,
 * `stt.*_delay` and `lk.e2e_latency`). So the values pass straight through and
 * only the axis formatter converts back to ms for its labels.
 */
export function SpanAttributePercentilesTimeSeries({
  projectId,
  timeRange,
  onTimeRangeSelected,
  attributePath,
  filterCondition,
}: SpanAttributePercentilesTimeSeriesProps) {
  const scale = useTimeBinScale({ timeRange });
  const utcOffsetMinutes = useUTCOffsetMinutes();

  const data = useLazyLoadQuery<SpanAttributePercentilesTimeSeriesQuery>(
    graphql`
      query SpanAttributePercentilesTimeSeriesQuery(
        $projectId: ID!
        $attributePath: [String!]!
        $timeRange: TimeRange!
        $timeBinConfig: TimeBinConfig!
        $filterCondition: String!
      ) {
        project: node(id: $projectId) {
          ... on Project {
            spanAttributePercentileTimeSeries(
              attributePath: $attributePath
              timeRange: $timeRange
              timeBinConfig: $timeBinConfig
              filterCondition: $filterCondition
            ) {
              data {
                timestamp
                p50
                p75
                p90
                p95
                p99
                p999
                max
              }
            }
          }
        }
      }
    `,
    {
      projectId,
      attributePath: [...attributePath],
      timeRange: {
        start: timeRange.start?.toISOString(),
        end: timeRange.end?.toISOString(),
      },
      timeBinConfig: {
        scale,
        utcOffsetMinutes,
      },
      filterCondition,
    },
    useMetricQueryFetchOptions()
  );

  const chartData = (
    data.project.spanAttributePercentileTimeSeries?.data ?? []
  ).map((datum) => ({
    timestamp: new Date(datum.timestamp).getTime(),
    p50: datum.p50,
    p75: datum.p75,
    p90: datum.p90,
    p95: datum.p95,
    p99: datum.p99,
    p999: datum.p999,
    max: datum.max,
  }));
  const hasData = chartData.some((datum) => datum.max != null);

  const timeTickFormatter = useBinTimeTickFormatter({ scale });

  const colors = useSequentialChartColors();
  const { hiddenDataKeys, isDataKeyHidden, toggleDataKey } =
    useInteractiveLegend();

  return (
    <TimeRangeChartBrush onTimeRangeSelected={onTimeRangeSelected}>
      {({ chartProps }) => (
        <ChartEmptyStateOverlay
          isEmpty={!hasData}
          message="No data in this time range"
          chartType="line"
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={compactChartMargin}
              syncId={PROJECT_METRICS_CHART_SYNC_ID}
              {...chartProps}
            >
              <CartesianGrid {...defaultCartesianGridProps} />
              <XAxis
                {...compactTimeXAxisProps}
                domain={[timeRange.start.getTime(), timeRange.end.getTime()]}
                tickFormatter={(x) => timeTickFormatter(new Date(x))}
              />
              <YAxis
                {...compactYAxisProps}
                tickFormatter={(seconds) => latencyMsFormatter(seconds * 1000)}
              />
              <Line
                type="monotone"
                dataKey="p50"
                stroke={colors.blue400}
                strokeWidth={1}
                activeDot={{ r: 4 }}
                name="P50"
                hide={isDataKeyHidden("p50")}
              />
              <Line
                type="monotone"
                dataKey="p75"
                stroke={colors.blue400}
                strokeWidth={1}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
                name="P75"
                hide={isDataKeyHidden("p75")}
              />
              <Line
                type="monotone"
                dataKey="p90"
                stroke={colors.blue500}
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
                name="P90"
                hide={isDataKeyHidden("p90")}
              />
              <Line
                type="monotone"
                dataKey="p95"
                stroke={colors.blue600}
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
                name="P95"
                hide={isDataKeyHidden("p95")}
              />
              <Line
                type="monotone"
                dataKey="p99"
                stroke={colors.blue700}
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
                name="P99"
                hide={isDataKeyHidden("p99")}
              />
              <Line
                type="monotone"
                dataKey="p999"
                stroke={colors.blue800}
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
                name="P99.9"
                hide={isDataKeyHidden("p999")}
              />
              <Line
                type="monotone"
                dataKey="max"
                stroke={colors.blue900}
                strokeWidth={2}
                strokeDasharray={"5 5"}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
                name="Max"
                hide={isDataKeyHidden("max")}
              />
              <InteractiveLegend
                {...compactLegendProps}
                hiddenDataKeys={hiddenDataKeys}
                iconType="line"
                iconSize={8}
                onToggleDataKey={toggleDataKey}
              />
              <Tooltip content={TooltipContent} {...defaultTooltipProps} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartEmptyStateOverlay>
      )}
    </TimeRangeChartBrush>
  );
}
